import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import upload from "../../Assets/images/drag.webp";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export default function DragandDrop({
  setImage,
  error,
  helperText,
  setFormikImage,
  currentImages = [],
}) {
  const { t, i18n } = useTranslation();
  const [remainingSlots, setRemainingSlots] = useState(
    3 - currentImages.length
  );

  useEffect(() => {
    setRemainingSlots(3 - currentImages.length);
  }, [currentImages]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/*": [],
    },
    multiple: true,
    disabled: remainingSlots <= 0,
    onDrop: (acceptedFiles) => {
      // If no slots remaining, show error and return
      if (remainingSlots <= 0) {
        toast.error(
          i18n.language === "en-US"
            ? "Maximum 3 images allowed."
            : "Maximal 3 Bilder erlaubt."
        );
        return;
      }

      // Calculate how many more files we can add
      const availableSlots = remainingSlots;

      // Start with existing images
      let merged = [...currentImages];
      let addedCount = 0;

      // Process new files up to available slots
      for (
        let i = 0;
        i < acceptedFiles?.length && addedCount < availableSlots;
        i++
      ) {
        const file = acceptedFiles[i];

        // Check file size and duplicates
        if (
          file.size <= 20 * 1024 * 1024 &&
          !merged.some((f) => f.name === file.name && f.size === file.size)
        ) {
          merged.push(file);
          addedCount++;
        } else if (file.size > 20 * 1024 * 1024) {
          toast.error(
            i18n.language === "en-US"
              ? "Selected file exceeds 20 MB limit."
              : "Die ausgewählte Datei überschreitet das 20-MB-Limit"
          );
        }
      }

      // If we tried to add more than available slots, show a message
      if (acceptedFiles.length > availableSlots) {
        toast.info(
          i18n.language === "en-US"
            ? `Only added ${addedCount} image(s). Maximum limit is 3 images total.`
            : `Nur ${addedCount} Bild(er) hinzugefügt. Maximales Limit sind insgesamt 3 Bilder.`
        );
      }

      setImage(merged); // Pass merged array to parent
      setFormikImage && setFormikImage();
    },
  });

  return (
    <div>
      <Box
        sx={{
          background: `#FFFAF5`,
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          justifyContent: "center",
          opacity: remainingSlots <= 0 ? 0.6 : 1, // Dim the dropzone if no slots available
          cursor: remainingSlots <= 0 ? "not-allowed" : "pointer",
        }}
        borderRadius="10px"
        border={error ? "1px dashed #d32f2f" : "1px dashed #F57C00"}
        p={2}
        m={"auto"}
        height={"220px"}
        width={"100%"}
        maxWidth={"650px"}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "center",
          }}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <figure>
            <img
              loading="lazy"
              src={upload}
              alt="logo.."
              width="80px"
              height="80px"
              draggable="false"
              style={{
                objectFit: "contain",
                borderRadius: "10px",
              }}
            />
          </figure>
          <Box
            sx={{
              display: { xs: "grid", lg: "flex" },
              alignItems: "center",
              justifyContent: "center",
              placeItems: "center",
              ":hover": {
                opacity: remainingSlots > 0 ? "0.72" : "1",
              },
            }}
          >
            <div style={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight="700" fontSize={"14px"}>
                {remainingSlots > 0
                  ? t("becomePartner.step2.foodTruckDetails.image.drag")
                  : i18n.language === "en-US"
                  ? "Maximum 3 images reached"
                  : "Maximale Anzahl von 3 Bildern erreicht"}
              </Typography>
              {remainingSlots > 0 && (
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {i18n.language === "en-US"
                    ? `You can add ${remainingSlots} more image${
                        remainingSlots !== 1 ? "s" : ""
                      }`
                    : `Sie können noch ${remainingSlots} Bild${
                        remainingSlots !== 1 ? "er" : ""
                      } hinzufügen`}
                </Typography>
              )}
            </div>
          </Box>
        </div>
      </Box>
      <Typography ml={2} color="#d32f2f" fontSize="0.75rem">
        {helperText && t("becomePartner.step2.foodTruckDetails.image.limit")}
      </Typography>
    </div>
  );
}
